import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import AppWrapper from "@/shared/ui/components/app-wrapper";
import { StickyFooterContent } from "@/modules/plan-selection/components/sticky-footer-content";

import { PaymentOptionCard } from "@/modules/plan-selection/components/payment-option-card";
import SubventionBanner from "@/shared/ui/components/subvention-banner";
import { PaymentDivider } from "@/modules/plan-selection/components/payment-divider";
import { InstalmentOptionPicker } from "@/modules/plan-selection/components/instalment-option-picker";
import { useTranslation } from "react-i18next";
import {
  LOCALIZED_ICON_TYPE,
  type CheckoutSession,
  CheckoutStatus,
  type AppContext,
  Currency,
  type PaymentOption,
} from "@/types";
import { OrderDetailsModal } from "@/modules/plan-selection/components/order-details-modal";
import {
  getRouteForSession,
  useCheckoutSessionV3,
  useIsOrderDetailsOpen,
  useModalsActions,
  usePaymentPlansActions,
  useSelectedPaymentOption,
  useSelectPaymentOption,
} from "@/modules/checkout-session";
import { type PpsPaymentOption } from "@/modules/checkout-session";
import { wait } from "@/lib/common";
import { encodeCurrency } from "@/lib/codec";
import * as events from "@/events";
import LoadingCheckout from "@/shared/ui/components/loading-checkout";
import { logError, logInfo } from "@/services/logging/logger";
import SplitPaymentUnavailable from "@/shared/ui/components/split-payment-unavailable";
import {
  emitPaymentPlanTracking,
  emitNoPaymentPlanTracking,
  emitPifShown,
  emitPnmShown,
  emitPaymentPlanSelectionTracking,
} from "@/shared/tracking/analytics";
import { useScreenTracking } from "@/shared/hooks/use-screen-tracking";
import { isExternalVerificationLoadingStatus } from "@/modules/external-verification/statuses";
import { DeclineUiFooter, DeclineUiScreen } from "@/modules/decline-ui";

// =============================================================================
// Types & Interfaces
// =============================================================================

interface PlanSelectionProps {
  onCheckoutFetched: (session: CheckoutSession) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const enum Processing {
  NONE,
  PAYMENT_OPTION,
}

interface PlanSelectionViewProps {
  ctx: AppContext;
  processing: Processing;
  hasCashbackBanner: boolean;
  hasAnySubvention: boolean;
  selectedOptionSubvention: {
    amount: number;
    currency: string | undefined;
    hasZeroFeesOrWaivedFees: boolean;
  } | null;
  session: CheckoutSession | undefined;
  handlePayInFullSelect: () => void;
  handlePayNextMonthSelect: () => void;
  isOrderDetailsOpen: boolean;
  setIsOrderDetailsOpen: (value: boolean) => void;
  setIsCartModalOpen: (value: boolean) => void;
  selectedPaymentOption: PpsPaymentOption | undefined;
  handleContinueToPayment: (option?: PpsPaymentOption) => Promise<void>;
}

type PaymentOptionWithExtras = PaymentOption & {
  cashback?: {
    eligibleForCashback?: boolean;
    actualAmount?: { amount: number };
    percent?: number;
  };
  appliedRewards?: {
    rewardAmount?: { amount: number };
  };
};

// =============================================================================
// Constants
// =============================================================================

const STATUSES_TO_POLL: CheckoutStatus[] = [
  CheckoutStatus.NEW,
  CheckoutStatus.PRE_UNDERWRITING_FRAUD_CHECK,
  CheckoutStatus.PRE_UDNERWRITING_FRAUD_CHECK,
  // Sits between CHECKING_QUESTIONNAIRE_REQUIRED and the pre-underwriting
  // fraud check; the user is still waiting on credit evaluation here.
  CheckoutStatus.CHECKING_OVERDUE_PAYMENTS,
  CheckoutStatus.EVALUATING_ORDER_CREDIT,
];

// Transient statuses that mean a payment option is already chosen and the
// backend is settling it (heading to payment method selection) — the user is
// no longer picking a plan. The backend surfaces these briefly before
// AWAIT_PAYMENT_METHOD_SELECTION (notably on single-page checkout, where the
// plan is auto-selected). They have no route of their own, so without this the
// plan screen paints during the gap before the redirect fires — the flicker.
const SETTLING_OPTION_STATUSES: CheckoutStatus[] = [
  CheckoutStatus.VALIDATING_PAYMENT_OPTION_SELECTION,
  CheckoutStatus.PAYMENT_OPTION_SELECTED,
  CheckoutStatus.PAYMENT_OPTION_VALIDATED,
];

const POLL_INTERVAL_MS = 3000;

// =============================================================================
// Utility Functions
// =============================================================================

const poll = async (
  getCheckoutSession: () => Promise<CheckoutSession>,
  onSessionFetched: (session: CheckoutSession) => void,
  statusCheck: CheckoutStatus,
) => {
  logInfo("payment-plan-selection", "GET request triggered to checkout/v3");
  const session = await getCheckoutSession();
  logInfo("payment-plan-selection", "GET response received from checkout/v3", {
    sessionId: session.id,
    status: session.status,
  });

  onSessionFetched(session);

  if (!shouldPoll(session) || statusCheck !== session.status) {
    return;
  }

  await wait(POLL_INTERVAL_MS);

  return await poll(getCheckoutSession, onSessionFetched, statusCheck);
};

const shouldPoll = (session: CheckoutSession) =>
  STATUSES_TO_POLL.includes(session?.status ?? CheckoutStatus.UNKNOWN) ||
  SETTLING_OPTION_STATUSES.includes(
    session?.status ?? CheckoutStatus.UNKNOWN,
  ) ||
  isExternalVerificationLoadingStatus(
    session?.status ?? CheckoutStatus.UNKNOWN,
  );

const isLoading = (ctx: AppContext) =>
  STATUSES_TO_POLL.includes(ctx.session.status) ||
  isExternalVerificationLoadingStatus(ctx.session.status);

// The session already resolves to a different route (e.g. single-page checkout
// lands on AWAIT_PAYMENT_METHOD_SELECTION -> /confirmation). Mirror the redirect
// handler's routing so we can show the loader instead of the plan UI while its
// checkout/v3 fetch resolves and navigation fires.
const willRedirectAway = (ctx: AppContext) => {
  const route = getRouteForSession(ctx.session);
  return route !== "" && route !== "/";
};

// The "/" route defaults to the plan-selection screen, but it must only paint
// when the user is actually choosing a plan. While the session is still loading,
// settling an already-chosen option, or about to be redirected elsewhere, show
// the loader instead — otherwise the plan screen flashes for a moment before it
// is replaced (the reported flicker).
const shouldDeferPlanScreen = (ctx: AppContext) =>
  isLoading(ctx) ||
  SETTLING_OPTION_STATUSES.includes(ctx.session.status) ||
  willRedirectAway(ctx);

// =============================================================================
// Custom Hooks
// =============================================================================

function useContentVisibility() {
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("hasNavigatedAway") === "true";
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => setIsVisible(true), 0);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const entries = performance.getEntriesByType(
      "navigation",
    ) as PerformanceNavigationTiming[];
    const isReload = entries.length > 0 && entries[0].type === "reload";

    if (isReload) {
      sessionStorage.removeItem("hasNavigatedAway");
    }
  }, []);

  return isVisible;
}

// =============================================================================
// Sub-components
// =============================================================================

function PlanSelectionView({
  ctx,
  processing,
  hasCashbackBanner,
  hasAnySubvention,
  selectedOptionSubvention,
  session,
  handlePayInFullSelect,
  handlePayNextMonthSelect,
  isOrderDetailsOpen,
  setIsOrderDetailsOpen,
  setIsCartModalOpen,
  selectedPaymentOption,
  handleContinueToPayment,
}: PlanSelectionViewProps) {
  const { t, i18n } = useTranslation();

  const paymentOptions = useMemo(
    () => ctx.session?.paymentOptions ?? [],
    [ctx.session?.paymentOptions],
  );
  const hasPayInFull = paymentOptions.some((opt) => opt.tenure === 0);
  const hasPayNextMonth = paymentOptions.some((opt) => opt.tenure === 1);
  const hasInstalments = paymentOptions.some((opt) => opt.tenure >= 2);
  const isPayInFullOnly = paymentOptions.length === 1 && hasPayInFull;
  const showPifOrPnm = hasPayInFull || hasPayNextMonth;

  const subventionCurrency = useMemo(
    () =>
      selectedOptionSubvention?.currency ??
      session?.order?.currency ??
      Currency.SAR,
    [selectedOptionSubvention?.currency, session?.order?.currency],
  );

  const hasZeroFeesOrWaivedFees = useMemo(
    () => selectedOptionSubvention?.hasZeroFeesOrWaivedFees ?? false,
    [selectedOptionSubvention?.hasZeroFeesOrWaivedFees],
  );

  const installmentTenureForModal = useMemo(() => {
    if (
      selectedPaymentOption?.type === "PAY_BY_INSTALMENTS" &&
      selectedPaymentOption.payments >= 2
    ) {
      return selectedPaymentOption.payments;
    }

    const installmentOptions = paymentOptions.filter((opt) => opt.tenure >= 2);
    const preSelectedInstalment = installmentOptions.find(
      (opt) => opt.preSelected === true,
    );
    if (preSelectedInstalment) {
      return preSelectedInstalment.tenure;
    }

    return installmentOptions[0]?.tenure ?? 0;
  }, [selectedPaymentOption, paymentOptions]);

  const choosePlanHeadingKey = "plan_page_improvements.plan.choose_plan";

  return (
    <>
      <div className={hasCashbackBanner ? "pb-64" : "pb-20"}>
        <h2
          className={`text-[24px] px-6 font-semibold text-gray-900 mb-6 transition-opacity ${processing !== Processing.NONE ? "opacity-50" : ""}`}>
          {isPayInFullOnly ? t("plan.pay_in_full") : t(choosePlanHeadingKey)}
        </h2>

        <div className="px-6">
          {hasInstalments && (
            <div className={`relative ${hasAnySubvention ? "mt-10" : ""}`}>
              <SubventionBanner
                amount={selectedOptionSubvention?.amount ?? 0}
                currency={subventionCurrency}
                language={i18n.language}
                isVisible={!selectedOptionSubvention}
                hasZeroFeesOrWaivedFees={hasZeroFeesOrWaivedFees}
              />
              <div className="relative z-10">
                <InstalmentOptionPicker
                  chevronIcon={ctx.localizedIcon(
                    LOCALIZED_ICON_TYPE.CHEVRON_FORWARD,
                  )}
                  ctx={ctx}
                  disabled={processing !== Processing.NONE}
                />
              </div>
            </div>
          )}

          {showPifOrPnm && hasInstalments && <PaymentDivider />}

          {hasPayNextMonth && (
            <PaymentOptionCard
              type="PAY_NEXT_MONTH"
              onSelect={handlePayNextMonthSelect}
              ctx={ctx}
              disabled={processing !== Processing.NONE}
            />
          )}

          {hasPayInFull && hasPayNextMonth && <div className="mt-9" />}

          {hasPayInFull && (
            <PaymentOptionCard
              type="PAY_NOW"
              onSelect={handlePayInFullSelect}
              ctx={ctx}
              disabled={processing !== Processing.NONE}
            />
          )}
        </div>

        {isPayInFullOnly && (
          <div className="px-6 mt-6">
            <SplitPaymentUnavailable />
          </div>
        )}
      </div>

      <OrderDetailsModal
        isOpen={isOrderDetailsOpen}
        onClose={() => setIsOrderDetailsOpen(false)}
        onContinue={() => setIsOrderDetailsOpen(false)}
        onShowCart={() => setIsCartModalOpen(true)}
        selectedPayments={installmentTenureForModal}
        ctx={ctx}
        handleContinueToPayment={handleContinueToPayment}
      />
    </>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function PlanSelection({
  onCheckoutFetched,
}: PlanSelectionProps) {
  const { data: session } = useCheckoutSessionV3();

  const selectedPaymentOption = useSelectedPaymentOption();
  const { setSelectedPaymentOption } = usePaymentPlansActions();

  const [processing, setProcessing] = useState<Processing>(Processing.NONE);
  const hasEmittedInitialEvent = useRef<boolean>(false);
  const hasEmittedPifShown = useRef<boolean>(false);
  const hasEmittedPnmShown = useRef<boolean>(false);
  const [payInFullJustClicked, setPayInFullJustClicked] =
    useState<boolean>(false);
  const [payNextMonthJustClicked, setPayNextMonthJustClicked] =
    useState<boolean>(false);

  const findPaymentOptionByTenure = useCallback(
    (tenure: number | undefined): PaymentOption | undefined => {
      if (tenure === undefined) return undefined;
      return session?.paymentOptions?.find(
        (option) => option.tenure === tenure,
      );
    },
    [session],
  );

  const getCashbackAndRewardForSelectedOption = useCallback(() => {
    const tenureToFind =
      selectedPaymentOption?.type === "PAY_NOW"
        ? 0
        : selectedPaymentOption?.payments;

    const selectedOption = findPaymentOptionByTenure(tenureToFind);

    const optionWithExtras = selectedOption as
      | PaymentOptionWithExtras
      | undefined;
    const cashback = optionWithExtras?.cashback;
    const eligible = cashback?.eligibleForCashback === true;
    const cashbackAmt = cashback?.actualAmount?.amount ?? 0;
    const cashbackPercent = cashback?.percent ?? 0;

    const appliedRewards = optionWithExtras?.appliedRewards;
    const appliedRewardsData = {
      rewardAmount: appliedRewards?.rewardAmount?.amount ?? 0,
      hasRewards: (appliedRewards?.rewardAmount?.amount ?? 0) > 0,
    };

    return {
      eligible,
      cashbackAmt,
      cashbackPercent,
      appliedRewardsData,
      appliedRewards,
      waivedOffReason: selectedOption?.waivedOffReason ?? null,
      waivedOffFeeAmount: selectedOption?.waivedOffFee?.amount ?? 0,
      processingFeeAmount: selectedOption?.processingFeeAmount?.amount ?? 0,
      isFeeWaivedOff: selectedOption?.isFeeWaivedOff ?? false,
    };
  }, [selectedPaymentOption, findPaymentOptionByTenure]);

  useEffect(() => {
    if (
      !session ||
      !session.paymentOptions ||
      session.paymentOptions.length === 0
    ) {
      return;
    }

    if (selectedPaymentOption) {
      return;
    }

    const preSelectedOption = session.paymentOptions.find(
      (option) => option.preSelected === true,
    );

    const paymentOptionsForDefault =
      session.paymentOptions
        ?.filter((option) => option.tenure >= 2)
        ?.map((option) => ({
          payments: option.tenure,
          noFees: option.totalCustomerFeeAmount.amount === 0,
        }))
        ?.sort((a, b) => (a.payments < b.payments ? -1 : 1)) ?? [];

    const installmentOptions = session.paymentOptions.filter(
      (option) => option.tenure >= 2,
    );

    if (installmentOptions.length === 0) {
      const payInFullAvailable = session.paymentOptions.some(
        (option) => option.tenure === 0,
      );
      if (!hasEmittedInitialEvent.current) {
        hasEmittedInitialEvent.current = true;
        emitNoPaymentPlanTracking(session, payInFullAvailable);
      }
      return;
    }

    const optionToSelect = preSelectedOption
      ? preSelectedOption
      : installmentOptions.reduce((max, option) =>
          option.tenure > max.tenure ? option : max,
        );

    const visualIndex = paymentOptionsForDefault.findIndex(
      (option) => option.payments === optionToSelect.tenure,
    );

    setSelectedPaymentOption({
      payments: optionToSelect.tenure,
      type: "PAY_BY_INSTALMENTS",
      visualIndex: visualIndex >= 0 ? visualIndex : 0,
    });

    if (!hasEmittedInitialEvent.current && optionToSelect && session) {
      hasEmittedInitialEvent.current = true;
      emitPaymentPlanTracking(
        "default_payment_plan_was_shown",
        optionToSelect,
        session,
      );
    }
  }, [session, selectedPaymentOption, setSelectedPaymentOption]);

  // Reset flags when user selects an installment or navigates back
  useEffect(() => {
    if (selectedPaymentOption?.type === "PAY_BY_INSTALMENTS") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPayInFullJustClicked(false);

      setPayNextMonthJustClicked(false);
    }

    if (selectedPaymentOption?.type === "PAY_NEXT_MONTH") {
      setPayInFullJustClicked(false);
    }

    if (selectedPaymentOption?.type === "PAY_NOW") {
      setPayNextMonthJustClicked(false);
    }
  }, [selectedPaymentOption]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPayInFullJustClicked(false);
      setPayNextMonthJustClicked(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const { trigger: selectPaymentOption } = useSelectPaymentOption();

  const ctxRef = useRef<AppContext | null>(null);
  const [screenCtx, setScreenCtx] = useState<AppContext | null>(null);
  const screenCtxRef = useRef<AppContext | null>(null);

  useEffect(() => {
    if (screenCtxRef.current && !screenCtx) {
      setScreenCtx(screenCtxRef.current);
    }
  }, [screenCtx]);

  useScreenTracking({
    screenName: "payment_plan_selection",
    viewedEvent: events.PAYMENT_PLAN_SELECTION_SCREEN_VIEWED,
    exitedEvent: events.PAYMENT_PLAN_SELECTION_SCREEN_EXITED,
    ctx: screenCtx,
  });

  useEffect(() => {
    if (
      !session ||
      !session.paymentOptions ||
      session.paymentOptions.length === 0
    ) {
      return;
    }

    const payInFullOption = findPaymentOptionByTenure(0);
    const payNextMonthOption = findPaymentOptionByTenure(1);

    if (payInFullOption && !hasEmittedPifShown.current) {
      hasEmittedPifShown.current = true;
      emitPifShown(payInFullOption, session);
    }

    if (payNextMonthOption && !hasEmittedPnmShown.current) {
      hasEmittedPnmShown.current = true;
      emitPnmShown(payNextMonthOption, session);
    }
  }, [session, findPaymentOptionByTenure]);

  // =============================================================================
  // Event Handlers
  // =============================================================================

  const handleContinueToPayment = useCallback(
    async (overridePaymentOption?: PpsPaymentOption) => {
      if (typeof window !== "undefined") {
        sessionStorage.setItem("hasNavigatedAway", "true");
      }

      const paymentOptionToUse = overridePaymentOption || selectedPaymentOption;

      ctxRef.current?.sendEvent(events.CONTINUE_CLICKED, {
        continue_from: "payment_plan",
        instalments: paymentOptionToUse?.payments ?? 0,
        payment_type: paymentOptionToUse?.type ?? "unknown",
      });

      if (session) {
        const paymentOption = findPaymentOptionByTenure(
          paymentOptionToUse?.payments,
        );
        emitPaymentPlanSelectionTracking(
          "continue_with_option_button_clicked",
          session,
          {
            instalments: paymentOptionToUse?.payments ?? 0,
            ...(paymentOptionToUse?.type && {
              payment_type: paymentOptionToUse.type,
            }),
            offer_id: paymentOption?.offerId,
            visual_index: paymentOptionToUse?.visualIndex,
          },
        );
      }

      const tenureToFind =
        paymentOptionToUse?.type === "PAY_NOW"
          ? 0
          : paymentOptionToUse?.payments;

      const paymentOption = findPaymentOptionByTenure(tenureToFind);

      const paymentOptionOfferId = paymentOption?.offerId;
      if (!paymentOptionOfferId) {
        logError(
          "payment-plan-selection",
          "Unexpected error: payment option offer id not found",
          undefined,
          { tenureToFind, paymentOption },
        );
        return;
      }

      setProcessing(Processing.PAYMENT_OPTION);

      try {
        logInfo(
          "payment-plan-selection",
          "POST request triggered to select-payment-option",
          {
            paymentOptionOfferId,
            tenure: paymentOption?.tenure,
          },
        );
        const data = await selectPaymentOption({
          paymentOptionOfferId,
          tenure: paymentOption?.tenure,
        });
        logInfo(
          "payment-plan-selection",
          "POST response received from select-payment-option",
          {
            sessionId: data.id,
            status: data.status,
          },
        );

        onCheckoutFetched(data);
      } catch (error) {
        setProcessing(Processing.NONE);
        throw error;
      }
    },
    [
      session,
      selectedPaymentOption,
      selectPaymentOption,
      onCheckoutFetched,
      findPaymentOptionByTenure,
    ],
  );

  const handlePayInFullSelect = useCallback(() => {
    if (session) {
      emitPaymentPlanSelectionTracking("pay_in_full_button_clicked", session, {
        payment_type: "PAY_NOW",
        instalments: 0,
      });
    }
    setPayInFullJustClicked(true);

    const payNowOption: PpsPaymentOption = { payments: 0, type: "PAY_NOW" };
    setSelectedPaymentOption(payNowOption);
    handleContinueToPayment(payNowOption).catch((error) => {
      logError(
        "payment-plan-selection",
        "Error continuing to payment (pay in full)",
        error instanceof Error ? error : new Error(String(error)),
        { error },
      );
    });
  }, [handleContinueToPayment, session, setSelectedPaymentOption]);

  const handlePayNextMonthSelect = useCallback(() => {
    if (session) {
      emitPaymentPlanSelectionTracking(
        "pay_next_month_button_clicked",
        session,
        {
          payment_type: "PAY_NEXT_MONTH",
          instalments: 1,
        },
      );
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tracker = (window as any)?.tracker;

    if (
      session &&
      tracker &&
      session.merchant?.id &&
      session.id &&
      session.order?.id &&
      session.customerId &&
      session.order?.countryCode
    ) {
      const currencyCode = encodeCurrency(session.order.currency) ?? "SAR";

      tracker.track({
        eventType: "SelectedPayNextMonth",
        merchantId: session.merchant.id,
        checkoutId: session.id,
        orderId: session.order.id,
        customerId: session.customerId,
        countryCode: session.order.countryCode,
        currencyCode,
        eventData: {},
      });
    }

    setPayNextMonthJustClicked(true);

    const payNextMonthOption: PpsPaymentOption = {
      payments: 1,
      type: "PAY_NEXT_MONTH",
    };
    setSelectedPaymentOption(payNextMonthOption);
    handleContinueToPayment(payNextMonthOption).catch((error) => {
      logError(
        "payment-plan-selection",
        "Error continuing to payment (pay next month)",
        error instanceof Error ? error : new Error(String(error)),
        { error },
      );
    });
  }, [handleContinueToPayment, session, setSelectedPaymentOption]);

  const mainContentVisible = useContentVisibility();
  const isOrderDetailsOpen = useIsOrderDetailsOpen();
  const { setIsOrderDetailsOpen, setIsCartModalOpen } = useModalsActions();

  const getCheckoutSessionRef = useRef<(() => Promise<CheckoutSession>) | null>(
    null,
  );

  useEffect(() => {
    if (getCheckoutSessionRef.current) {
      poll(
        getCheckoutSessionRef.current!,
        onCheckoutFetched,
        session?.status ?? CheckoutStatus.UNKNOWN,
      );
    }
  }, [session?.status, onCheckoutFetched]);

  const selectedOptionSubvention = useMemo(() => {
    const selectedOption = findPaymentOptionByTenure(
      selectedPaymentOption?.payments,
    );
    if (!selectedOption?.subvention?.applied) return null;
    const subventionAmount =
      selectedOption.subvention.appliedTotalAmount?.amount ?? 0;
    if (subventionAmount <= 0) return null;

    const hasZeroFeesOrWaivedFees =
      selectedOption.totalCustomerFeeAmount.amount === 0 ||
      (selectedOption.waivedOffFee?.amount ?? 0) > 0;

    return {
      amount: subventionAmount,
      currency: selectedOption.subvention.appliedTotalAmount?.currency,
      hasZeroFeesOrWaivedFees,
    };
  }, [selectedPaymentOption, findPaymentOptionByTenure]);

  const hasAnySubvention = useMemo(() => {
    return (session?.paymentOptions ?? []).some(
      (option) => option.subvention?.applied,
    );
  }, [session]);

  const hasCashbackBanner = useMemo(
    () => getCashbackAndRewardForSelectedOption().eligible,
    [getCashbackAndRewardForSelectedOption],
  );

  return (
    <>
      <AppWrapper
        visible={mainContentVisible}
        disableHeaderInteractions={processing !== Processing.NONE}
        stickyFooter={(ctx) => {
          if (shouldDeferPlanScreen(ctx)) {
            return null;
          }

          if (ctx.session.creditDeclineInfo) {
            return (
              <DeclineUiFooter
                ctx={ctx}
                onPayInFull={handlePayInFullSelect}
                processing={processing !== Processing.NONE}
              />
            );
          }

          return (
            <StickyFooterContent
              ctx={ctx}
              cashbackData={getCashbackAndRewardForSelectedOption()}
              selectedPaymentOption={selectedPaymentOption}
              processing={processing}
              onContinue={handleContinueToPayment}
              payInFullJustClicked={payInFullJustClicked}
              payNextMonthJustClicked={payNextMonthJustClicked}
            />
          );
        }}
      >
        {(ctx) => {
          ctxRef.current = ctx;
          getCheckoutSessionRef.current = ctx.getCheckoutSession;

          // Only "activate" the plan screen (which fires the
          // PAYMENT_PLAN_SELECTION_SCREEN_VIEWED event) when we are actually
          // going to show it. Skipping this while deferred avoids phantom
          // viewed/exited events for sessions that never see the plan UI.
          if (!screenCtxRef.current && ctx && !shouldDeferPlanScreen(ctx)) {
            screenCtxRef.current = ctx;
          }

          if (shouldDeferPlanScreen(ctx)) return <LoadingCheckout ctx={ctx} />;

          if (ctx.session.creditDeclineInfo) {
            return <DeclineUiScreen ctx={ctx} />;
          }

          return (
            <PlanSelectionView
              ctx={ctx}
              processing={processing}
              hasCashbackBanner={hasCashbackBanner}
              hasAnySubvention={hasAnySubvention}
              selectedOptionSubvention={selectedOptionSubvention}
              session={session}
              handlePayInFullSelect={handlePayInFullSelect}
              handlePayNextMonthSelect={handlePayNextMonthSelect}
              isOrderDetailsOpen={isOrderDetailsOpen}
              setIsOrderDetailsOpen={setIsOrderDetailsOpen}
              setIsCartModalOpen={setIsCartModalOpen}
              selectedPaymentOption={selectedPaymentOption}
              handleContinueToPayment={handleContinueToPayment}
            />
          );
        }}
      </AppWrapper>
    </>
  );
}
